import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { addSeconds, differenceInMilliseconds } from 'date-fns';

import { PopupNames } from 'contexts/PopupContext';

import { Funnels, getFunnelSearchString } from 'helpers/funnels/funnels';
import {
  isMonetizationPage,
  isTermsOrPolicyPage,
  isUserVideoPage,
} from 'helpers/route';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from 'helpers/sessionStorage';
import { usePopup } from 'hooks/usePopup';
import { useUpgradePageTransition } from 'hooks/useUpgradePageTransition';
import { getUser } from 'store/auth/selectors';
import {
  isPaywallMonthPackageEnabledSelector,
  isRetentionFunnelsEnabledSelector,
} from 'store/common/selectors';

enum RetentionFunnels {
  MyVisitsPopupFunnel = 'myVisitsPopup',
  MyLikesPopupFunnel = 'myLikesPopup',
  UpgradeTrialPageFunnel = 'upgradeTrialPage',
}

interface StorageData {
  [RetentionFunnels.MyVisitsPopupFunnel]: {
    isShown: boolean;
    ts: Date | null;
  };
  [RetentionFunnels.MyLikesPopupFunnel]: {
    isShown: boolean;
    ts: Date | null;
  };
  [RetentionFunnels.UpgradeTrialPageFunnel]: {
    isShown: boolean;
    ts: Date | null;
  };
}

const RETENTION_FUNNELS_STORAGE_KEY = 'RETENTION_FUNNELS';
const MY_VIEWS_POPUP_DELAY = 30; // sec
const MY_LIKES_POPUP_DELAY = 60; // sec
const UPGRADE_TRIAL_PAGE_DELAY = 60; // sec

const isMyVisitsPopupFunnel = (retentionFunnel: RetentionFunnels) => {
  return retentionFunnel === RetentionFunnels.MyVisitsPopupFunnel;
};

export const getRetentionFunnelsDataFromStorage = (): StorageData | null => {
  const data = getSessionStorageItem(RETENTION_FUNNELS_STORAGE_KEY);

  return data ? JSON.parse(data) : null;
};

const setRetentionFunnelsDataToStorage = (data: StorageData | null) => {
  setSessionStorageItem(RETENTION_FUNNELS_STORAGE_KEY, JSON.stringify(data));
};

const getRetentionFunnelData = (
  storageData: StorageData | null,
  retentionFunnel: RetentionFunnels
) => {
  if (!storageData) {
    return {
      isShown: false,
      ts: null,
    };
  }

  return {
    isShown: storageData[retentionFunnel].isShown,
    ts: storageData[retentionFunnel].ts
      ? new Date(storageData[retentionFunnel].ts as Date)
      : null,
  };
};

export const setInitialDataToStorage = () => {
  const currentDate = new Date();
  setRetentionFunnelsDataToStorage({
    [RetentionFunnels.MyVisitsPopupFunnel]: {
      isShown: false,
      ts: new Date(addSeconds(currentDate, MY_VIEWS_POPUP_DELAY)),
    },
    [RetentionFunnels.MyLikesPopupFunnel]: {
      isShown: false,
      ts: null,
    },
    [RetentionFunnels.UpgradeTrialPageFunnel]: {
      isShown: false,
      ts: null,
    },
  });
};

export const useRetentionFunnels = () => {
  const history = useHistory();
  const location = useLocation();

  const isRetentionFunnelsEnabled = useSelector(
    isRetentionFunnelsEnabledSelector
  );
  const isStopExistEnable = useSelector(isPaywallMonthPackageEnabledSelector);
  const user = useSelector(getUser);

  const [isInited, setIsInited] = useState(false);
  const myViewsPopupTimer = useRef<NodeJS.Timeout | null>(null);
  const myLikesPopupTimer = useRef<NodeJS.Timeout | null>(null);
  const upgradeTrialPageTimer = useRef<NodeJS.Timeout | null>(null);
  const isPaymentPageOpened = useRef<boolean>(false);

  const { handleSafePushUpgradePage } = useUpgradePageTransition();

  const isPagesOpen =
    isMonetizationPage(location.pathname) ||
    isTermsOrPolicyPage(location.pathname) ||
    isUserVideoPage(location.pathname);

  const {
    handleOpenASAP: handleMyLikesPopupOpen,
    handleClose: handleMyLikesPopupClose,
    handleRemoveFromQueue: handleMyLikesPopupRemoveFromQueue,
    handleAction: handleMyLikesPopupAction,
  } = usePopup(PopupNames.MyLikes);

  const {
    handleOpenASAP: handleMyVisitsPopupOpen,
    handleClose: handleMyVisitsPopupClose,
    handleRemoveFromQueue: handleMyVisitsPopupRemoveFromQueue,
    handleAction: handleMyVisitsPopupAction,
    isAnyPopupOpen,
  } = usePopup(PopupNames.MyVisits);

  const handleOpenUpgradeTrialPage = useCallback(() => {
    const storageData = getRetentionFunnelsDataFromStorage();
    const search = isStopExistEnable
      ? getFunnelSearchString(Funnels.StopExit)
      : getFunnelSearchString(Funnels.StartDayTrial);

    if (storageData) {
      setRetentionFunnelsDataToStorage({
        ...storageData,
        [RetentionFunnels.UpgradeTrialPageFunnel]: {
          ...storageData[RetentionFunnels.UpgradeTrialPageFunnel],
          isShown: true,
        },
      });
    }

    history.push({
      pathname: '/upgrade_trial',
      search,
    });
  }, [history, isStopExistEnable]);

  const handleOpenPopup = useCallback(
    (
      funnel:
        | RetentionFunnels.MyVisitsPopupFunnel
        | RetentionFunnels.MyLikesPopupFunnel
    ) => {
      const storageData = getRetentionFunnelsDataFromStorage();

      if (storageData) {
        setRetentionFunnelsDataToStorage({
          ...storageData,
          [funnel]: {
            ...storageData[funnel],
            isShown: true,
          },
        });
      }

      const isShown = (storageData && storageData[funnel].isShown) || false;
      if (isMyVisitsPopupFunnel(funnel) && !isShown) {
        handleMyVisitsPopupOpen(null);
      }

      if (funnel === RetentionFunnels.MyLikesPopupFunnel && !isShown) {
        handleMyLikesPopupOpen(null);
      }
    },
    [handleMyLikesPopupOpen, handleMyVisitsPopupOpen]
  );

  handleMyVisitsPopupAction.current = (action: {
    type: string;
    user: { photo_url: string };
  }) => {
    const currentDate = new Date();
    const storageData = getRetentionFunnelsDataFromStorage();

    if (action.type === 'submit') {
      const { photo_url: profilePhotoUrl } = action.user;

      const search = getFunnelSearchString(Funnels.ActivityVisit, {
        photo: profilePhotoUrl,
        userId: null,
      });

      handleMyVisitsPopupClose();
      handleSafePushUpgradePage(search, {
        modal: RetentionFunnels.MyLikesPopupFunnel,
      });
    } else if (action.type === 'close') {
      handleMyVisitsPopupClose();
    }

    if (storageData) {
      setRetentionFunnelsDataToStorage({
        ...storageData,
        [RetentionFunnels.MyLikesPopupFunnel]: {
          ...storageData[RetentionFunnels.MyLikesPopupFunnel],
          ts: new Date(addSeconds(currentDate, MY_LIKES_POPUP_DELAY)),
        },
      });
    }
  };

  const handleActivateNextPopup = (type: RetentionFunnels) => {
    const currentDate = new Date();
    const storageData = getRetentionFunnelsDataFromStorage();

    if (storageData) {
      setRetentionFunnelsDataToStorage({
        ...storageData,
        [type]: {
          ...storageData[type],
          ts: new Date(addSeconds(currentDate, MY_LIKES_POPUP_DELAY)),
        },
      });

      const { ts: myLikesPopupTS } = getRetentionFunnelData(storageData, type);

      if (myLikesPopupTS && type === RetentionFunnels.MyLikesPopupFunnel) {
        const initialDelay = differenceInMilliseconds(
          myLikesPopupTS,
          currentDate
        );
        myLikesPopupTimer.current = setTimeout(
          () => handleOpenPopup(type),
          initialDelay > 0 ? initialDelay : 0
        );
      }
    }
  };

  handleMyLikesPopupAction.current = (action: {
    type: string;
    user: { photo_url: string };
  }) => {
    const currentDate = new Date();
    const storageData = getRetentionFunnelsDataFromStorage();
    if (action.type === 'submit') {
      const { photo_url: profilePhotoUrl } = action.user;

      const search = getFunnelSearchString(Funnels.ActivityLike, {
        photo: profilePhotoUrl,
        userId: null,
      });

      handleMyLikesPopupClose();
      handleSafePushUpgradePage(search, {
        modal: RetentionFunnels.UpgradeTrialPageFunnel,
      });
    } else if (action.type === 'close') {
      handleMyLikesPopupClose();
    }

    if (storageData) {
      setRetentionFunnelsDataToStorage({
        ...storageData,
        [RetentionFunnels.UpgradeTrialPageFunnel]: {
          ...storageData[RetentionFunnels.UpgradeTrialPageFunnel],
          ts: new Date(addSeconds(currentDate, UPGRADE_TRIAL_PAGE_DELAY)),
        },
      });
    }
  };

  useEffect(() => {
    const storageData = getRetentionFunnelsDataFromStorage();

    if (isRetentionFunnelsEnabled) {
      if (!storageData) {
        const currentDate = new Date();
        setRetentionFunnelsDataToStorage({
          [RetentionFunnels.MyVisitsPopupFunnel]: {
            isShown: false,
            ts: new Date(addSeconds(currentDate, MY_VIEWS_POPUP_DELAY)),
          },
          [RetentionFunnels.MyLikesPopupFunnel]: {
            isShown: false,
            ts: null,
          },
          [RetentionFunnels.UpgradeTrialPageFunnel]: {
            isShown: false,
            ts: null,
          },
        });
      }

      setIsInited(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRetentionFunnelsEnabled]);

  useEffect(() => {
    if (user?.is_premium) {
      setRetentionFunnelsDataToStorage(null);
      handleMyVisitsPopupRemoveFromQueue();
      handleMyLikesPopupRemoveFromQueue();

      setIsInited(false);
    }
  }, [user?.is_premium]);

  useEffect(() => {
    const storageData = getRetentionFunnelsDataFromStorage();
    const currentDate = new Date();
    const { isShown: isMyVisitsPopupShown, ts: myViewsPopupTS } =
      getRetentionFunnelData(storageData, RetentionFunnels.MyVisitsPopupFunnel);
    const { isShown: isMyLikesPopupShown, ts: myLikesPopupTS } =
      getRetentionFunnelData(storageData, RetentionFunnels.MyLikesPopupFunnel);
    const { isShown: isUpgradeTrialPageShown, ts: upgradeTrialPageTS } =
      getRetentionFunnelData(
        storageData,
        RetentionFunnels.UpgradeTrialPageFunnel
      );

    if (
      isInited &&
      myViewsPopupTS &&
      !isMyVisitsPopupShown &&
      !myViewsPopupTimer.current
    ) {
      const initialDelay = differenceInMilliseconds(
        myViewsPopupTS,
        currentDate
      );
      myViewsPopupTimer.current = setTimeout(
        () => handleOpenPopup(RetentionFunnels.MyVisitsPopupFunnel),
        initialDelay > 0 ? initialDelay : 0
      );
    }

    if (
      isInited &&
      myLikesPopupTS &&
      !isMyLikesPopupShown &&
      !myLikesPopupTimer.current
    ) {
      const initialDelay = differenceInMilliseconds(
        myLikesPopupTS,
        currentDate
      );
      myLikesPopupTimer.current = setTimeout(
        () => handleOpenPopup(RetentionFunnels.MyLikesPopupFunnel),
        initialDelay > 0 ? initialDelay : 0
      );
    }

    if (
      isInited &&
      upgradeTrialPageTS &&
      !isUpgradeTrialPageShown &&
      !upgradeTrialPageTimer.current
    ) {
      if (isPagesOpen) isPaymentPageOpened.current = true;
      if (
        !isMonetizationPage(location.pathname) &&
        !isTermsOrPolicyPage(location.pathname) &&
        !isUserVideoPage(location.pathname) &&
        !isAnyPopupOpen()
      ) {
        const defaultDelay = differenceInMilliseconds(
          upgradeTrialPageTS,
          new Date()
        );
        const initialDelay =
          isPaymentPageOpened.current &&
          isMyLikesPopupShown &&
          defaultDelay > 10000
            ? defaultDelay
            : 10000;

        upgradeTrialPageTimer.current = setTimeout(
          handleOpenUpgradeTrialPage,
          initialDelay > 0 ? initialDelay : 0
        );
      }
    }

    return () => {
      if (myViewsPopupTimer.current) {
        clearTimeout(myViewsPopupTimer.current);
        myViewsPopupTimer.current = null;
      }

      if (myLikesPopupTimer.current) {
        clearTimeout(myLikesPopupTimer.current);
        myLikesPopupTimer.current = null;
      }

      if (upgradeTrialPageTimer.current) {
        clearTimeout(upgradeTrialPageTimer.current);
        upgradeTrialPageTimer.current = null;
      }
    };
  }, [handleOpenPopup, handleOpenUpgradeTrialPage, history, isInited]);

  return {
    handleActivateNextPopup,
  };
};
